import type { CreatePayload } from '@atlaskit/link-create';
import type { EMBEDDED_CONFLUENCE_MODE } from '@atlassian/embedded-confluence-common';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';

export const ConfluenceContentTypes = {
	WHITEBOARD: 'whiteboard',
	PAGE: 'page',
} as const;
export const DisplayTypes = {
	MODAL: 'modal',
	PANEL: 'panel',
} as const;

type EmbeddedConfluenceContentType =
	(typeof ConfluenceContentTypes)[keyof typeof ConfluenceContentTypes];
type EmbeddedConfluenceDisplayType = (typeof DisplayTypes)[keyof typeof DisplayTypes];

export type ConfluenceData = {
	contentUrl: string;
	contentId?: string;
	contentData?: CreatePayload | null;
	hostname?: string;
	isDraft?: boolean;
	isLive?: boolean;
	mode?: EMBEDDED_CONFLUENCE_MODE;
	title?: string;
	pageSpaceName?: string;
	deferredSpaceName?: string;
	spaceKey?: string;
};
export interface EmbeddedConfluenceState {
	confluenceData: ConfluenceData;
	contentActions: {
		onPublish?: () => void;
		onLinkPage?: (link: string, analyticsEvent: UIAnalyticsEvent) => void;
	};
	contentType: EmbeddedConfluenceContentType;
	embeddedConfluenceSource?: string;
	displayType: EmbeddedConfluenceDisplayType;
	isDeferSpaceSelection?: boolean;
	panel: {
		initialWidth: number;
		isOpen: boolean;
		isLoading?: boolean;
		isExpanded?: boolean;
	};
	modal: {
		isOpen: boolean;
	};
}

export type UseEmbeddedConfluenceSidePanelType = (embeddedConfluenceSource?: string) => [
	state: EmbeddedConfluenceState,
	actions: {
		closeSidePanel: () => void;
		openSidePanel: (params: OpenSidePanelCoreParams) => void;
		setIsLoadingForDeferSpaceSelection: (
			params: SetIsLoadingForDeferSpaceSelectionActionParams,
		) => void;
		setDeferredSpaceData: (name?: string, spaceKey?: string) => void;
		toggleExpandedState: () => void;
		setIsDraftState: (arg: { newIsDraftState: boolean }) => void;
		setConfluenceData: (arg: ConfluenceData) => void;
	},
];
export interface OpenSidePanelCoreParams {
	contentType: EmbeddedConfluenceContentType;
	openInModal: () => void;
	contentId?: string;
	data?: CreatePayload | null;
	hostname?: string;
	isDraft?: boolean;
	isLive?: boolean;
	isExpanded?: boolean;
	mode?: EMBEDDED_CONFLUENCE_MODE;
	onLinkContent?: (url: string) => void;
	onLinkPage?: (url: string, analyticsEvent: UIAnalyticsEvent) => void;
	onPublish?: () => void;
	title?: string;
	url: string;
}
export interface OpenSidePanelActionParams extends Omit<OpenSidePanelCoreParams, 'openInModal'> {
	displayType: (typeof DisplayTypes)[keyof typeof DisplayTypes];
	initialWidth: number;
	shouldOpenModal: boolean;
	embeddedConfluenceSource?: string;
}

export interface SetIsLoadingForDeferSpaceSelectionActionParams {
	isLoading: boolean;
	isDeferSpaceSelection?: boolean;
}
